import { D, Decimal, ONE, type DecimalInput } from './decimal.js';

/**
 * Analyse dimensionnelle : exposants des grandeurs de base.
 * L = longueur (m), M = masse (kg), T = temps (h), CUR = monnaie.
 * Les unités de comptage (U, sac, barre, rouleau, forfait…) sont sans dimension.
 */
export interface Dimension {
  L: number;
  M: number;
  T: number;
  CUR: number;
}

export const DIMENSIONLESS: Dimension = Object.freeze({ L: 0, M: 0, T: 0, CUR: 0 });
const KEYS = ['L', 'M', 'T', 'CUR'] as const;

export function dim(partial: Partial<Dimension> = {}): Dimension {
  return { L: 0, M: 0, T: 0, CUR: 0, ...partial };
}
export function dimMul(a: Dimension, b: Dimension): Dimension {
  return { L: a.L + b.L, M: a.M + b.M, T: a.T + b.T, CUR: a.CUR + b.CUR };
}
export function dimDiv(a: Dimension, b: Dimension): Dimension {
  return { L: a.L - b.L, M: a.M - b.M, T: a.T - b.T, CUR: a.CUR - b.CUR };
}
export function dimPow(a: Dimension, n: number): Dimension {
  return { L: a.L * n, M: a.M * n, T: a.T * n, CUR: a.CUR * n };
}
export function dimEquals(a: Dimension, b: Dimension): boolean {
  return KEYS.every((k) => a[k] === b[k]);
}
export function isDimensionless(a: Dimension): boolean {
  return dimEquals(a, DIMENSIONLESS);
}
/** Représentation lisible : « m³ », « kg·m⁻³ », « sans dimension ». */
export function formatDimension(d: Dimension): string {
  const sup: Record<string, string> = { '-': '⁻', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹', '0': '⁰' };
  const names: Record<(typeof KEYS)[number], string> = { L: 'm', M: 'kg', T: 'h', CUR: 'monnaie' };
  const parts = KEYS.filter((k) => d[k] !== 0).map((k) =>
    d[k] === 1 ? names[k] : names[k] + String(d[k]).split('').map((c) => sup[c] ?? c).join(''),
  );
  return parts.length ? parts.join('·') : 'sans dimension';
}

export type UnitCategory =
  | 'LENGTH'
  | 'AREA'
  | 'VOLUME'
  | 'MASS'
  | 'COUNT'
  | 'TIME'
  | 'LUMP_SUM'
  | 'CURRENCY'
  | 'RATIO';

export interface UnitDefinition {
  code: string;
  label: string;
  /** Symbole affiché (« m³ »). */
  symbol: string;
  category: UnitCategory;
  dimension: Dimension;
  /** Facteur vers l'unité de base de la dimension (cm → 0,01 m). */
  factor: Decimal;
  /** Décimales d'affichage par défaut. */
  displayDecimals: number;
  aliases?: string[];
}

function u(
  code: string,
  label: string,
  symbol: string,
  category: UnitCategory,
  dimension: Dimension,
  factor: DecimalInput,
  displayDecimals: number,
  aliases: string[] = [],
): UnitDefinition {
  return { code, label, symbol, category, dimension, factor: D(factor), displayDecimals, aliases };
}

const LEN = dim({ L: 1 });
const AREA = dim({ L: 2 });
const VOL = dim({ L: 3 });
const MASS = dim({ M: 1 });
const TIME = dim({ T: 1 });
const CUR = dim({ CUR: 1 });

/**
 * Bibliothèque d'unités par défaut (cahier des charges §33). Elle est extensible :
 * les unités ajoutées en base sont fournies au moteur via `UnitRegistry.with()`.
 */
export const DEFAULT_UNITS: readonly UnitDefinition[] = [
  u('m', 'mètre', 'm', 'LENGTH', LEN, 1, 2, ['M']),
  u('ml', 'mètre linéaire', 'ml', 'LENGTH', LEN, 1, 2, ['ML', 'm.l', 'mL']),
  u('cm', 'centimètre', 'cm', 'LENGTH', LEN, '0.01', 1),
  u('mm', 'millimètre', 'mm', 'LENGTH', LEN, '0.001', 0),
  u('km', 'kilomètre', 'km', 'LENGTH', LEN, 1000, 3),
  u('m2', 'mètre carré', 'm²', 'AREA', AREA, 1, 2, ['m²', 'M2', 'M²']),
  u('m3', 'mètre cube', 'm³', 'VOLUME', VOL, 1, 3, ['m³', 'M3', 'M³']),
  u('L', 'litre', 'L', 'VOLUME', VOL, '0.001', 1, ['l', 'litre']),
  u('kg', 'kilogramme', 'kg', 'MASS', MASS, 1, 2, ['KG', 'Kg']),
  u('t', 'tonne', 't', 'MASS', MASS, 1000, 3, ['T', 'tonne']),
  u('U', 'unité', 'U', 'COUNT', DIMENSIONLESS, 1, 0, ['u', 'unité', 'Unité', 'pce']),
  u('sac', 'sac', 'sac', 'COUNT', DIMENSIONLESS, 1, 2, ['Sac', 'sacs']),
  u('barre', 'barre', 'barre', 'COUNT', DIMENSIONLESS, 1, 2, ['Barre', 'barres']),
  u('rouleau', 'rouleau', 'rouleau', 'COUNT', DIMENSIONLESS, 1, 0, ['rlx']),
  u('voyage', 'voyage', 'voyage', 'COUNT', DIMENSIONLESS, 1, 0),
  u('FF', 'forfait', 'FF', 'LUMP_SUM', DIMENSIONLESS, 1, 0, ['ff', 'forfait', 'Forfait']),
  u('ENS', 'ensemble', 'ENS', 'LUMP_SUM', DIMENSIONLESS, 1, 0, ['ens', 'Ens', 'ensemble']),
  u('h', 'heure', 'h', 'TIME', TIME, 1, 2, ['heure', 'H']),
  // Journée de travail : 8 heures par convention (paramétrable en remplaçant l'unité).
  u('j', 'jour', 'j', 'TIME', TIME, 8, 2, ['jour', 'J']),
  u('%', 'pourcentage', '%', 'RATIO', DIMENSIONLESS, '0.01', 2),
  u('XAF', 'franc CFA (CEMAC)', 'FCFA', 'CURRENCY', CUR, 1, 0, ['FCFA', 'F']),
  u('XOF', 'franc CFA (UEMOA)', 'FCFA', 'CURRENCY', CUR, 1, 0),
  u('EUR', 'euro', '€', 'CURRENCY', CUR, 1, 2),
  u('USD', 'dollar US', '$', 'CURRENCY', CUR, 1, 2),
];

export class UnknownUnitError extends Error {
  constructor(public readonly unit: string) {
    super(`Unité inconnue : « ${unit} »`);
    this.name = 'UnknownUnitError';
  }
}

export interface ResolvedUnit {
  /** Expression d'origine (« kg/m3 »). */
  expression: string;
  dimension: Dimension;
  /** Facteur vers les unités de base. */
  factor: Decimal;
  /** Symbole affichable. */
  symbol: string;
  displayDecimals: number;
}

export class UnitRegistry {
  private readonly byCode = new Map<string, UnitDefinition>();

  constructor(units: Iterable<UnitDefinition> = DEFAULT_UNITS) {
    for (const unit of units) this.add(unit);
  }

  /** Nouveau registre enrichi (les unités personnalisées surchargent les codes existants). */
  with(units: Iterable<UnitDefinition>): UnitRegistry {
    const next = new UnitRegistry(this.byCode.values());
    for (const unit of units) next.add(unit);
    return next;
  }

  private add(unit: UnitDefinition): void {
    this.byCode.set(unit.code, unit);
    for (const alias of unit.aliases ?? []) if (!this.byCode.has(alias)) this.byCode.set(alias, unit);
  }

  get(code: string): UnitDefinition | undefined {
    return this.byCode.get(code.trim());
  }

  require(code: string): UnitDefinition {
    const unit = this.get(code);
    if (!unit) throw new UnknownUnitError(code);
    return unit;
  }

  list(): UnitDefinition[] {
    return [...new Set(this.byCode.values())];
  }

  /**
   * Résout une expression d'unité composée : « m3 », « kg/m3 », « kg/sac », « U/m2 », « FCFA/m3 », « m*j ».
   */
  resolve(expression: string): ResolvedUnit {
    const expr = expression.trim();
    const single = this.get(expr);
    if (single) {
      return { expression: expr, dimension: single.dimension, factor: single.factor, symbol: single.symbol, displayDecimals: single.displayDecimals };
    }
    const tokens = expr.split(/([*/·])/).map((t) => t.trim()).filter(Boolean);
    let dimension = DIMENSIONLESS;
    let factor = ONE;
    let op: '*' | '/' = '*';
    const symbols: string[] = [];
    for (const token of tokens) {
      if (token === '*' || token === '·') { op = '*'; symbols.push('·'); continue; }
      if (token === '/') { op = '/'; symbols.push('/'); continue; }
      const m = /^(.+?)\^(-?\d+)$/.exec(token);
      const code = m ? m[1]! : token;
      const power = m ? Number(m[2]) : 1;
      const unit = this.require(code);
      const d = dimPow(unit.dimension, power);
      const f = unit.factor.pow(power);
      dimension = op === '*' ? dimMul(dimension, d) : dimDiv(dimension, d);
      factor = op === '*' ? factor.mul(f) : factor.div(f);
      symbols.push(power === 1 ? unit.symbol : `${unit.symbol}^${power}`);
    }
    if (!symbols.length) throw new UnknownUnitError(expression);
    return { expression: expr, dimension, factor, symbol: symbols.join(''), displayDecimals: 3 };
  }
}

export const defaultUnits = new UnitRegistry();
